import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { OrderResponse, PatchRequest } from '../../../data/models'
import { WebEdiDto } from '../../../data/models/WebEdiDto'
import { useOrder } from '../data'
import { requestPatchOrderById } from '../data/api'
import { OrderConfirmationDto, OrderConfirmationPatchDto } from '../data/model'
import { Attachment } from './Attachment'
import { Header } from './Header'
import { OrderConfirmation } from './OrderConfirmation'
import { OrderInformation } from './OrderInformation'
import { Positions } from './Positions'
import { Title } from './Title'

type Props = {
  id: string
}
export const Order: React.FC<Props> = ({ id }) => {
  const { t } = useTranslation()
  const { data, isLoading, error, mutate } = useOrder(id)
  const navigate = useNavigate()

  const dataToFormValues = (
    data?: WebEdiDto<OrderResponse>,
  ): OrderConfirmationDto => {
    return {
      responsePeriod: data?.Payload.ResponsePeriod || { Start: '', End: '' },
      confirmationDate: data?.Payload.ConfirmationDate || '',
      confirmationId: data?.Payload.ConfirmationId || '',
      confirmationCode: data?.Payload.ConfirmationCode,
      department: data?.Payload.SellerInformation.Department || '',
      name: data?.Payload.SellerInformation.Contact?.Name || '',
      phone: data?.Payload.SellerInformation.Contact?.Phone || '',
      email: data?.Payload.SellerInformation.Contact?.Email || '',
      remarks: data?.Payload.Remarks || '',
    }
  }

  const patchFromFormValues = (
    formData: OrderConfirmationPatchDto,
  ): PatchRequest<WebEdiDto<OrderResponse>> => {
    const patchOperations: PatchRequest<WebEdiDto<OrderResponse>> = []

    if (formData.confirmationId !== data!.Payload.ConfirmationId) {
      patchOperations.push({
        op: data!.Payload.ConfirmationId ? 'replace' : 'add',
        path: '/Payload/ConfirmationId',
        value: formData.confirmationId,
      })
    }

    if (formData.confirmationCode !== data!.Payload.ConfirmationCode) {
      patchOperations.push({
        op: data!.Payload.ConfirmationCode ? 'replace' : 'add',
        path: '/Payload/ConfirmationCode',
        value: formData.confirmationCode,
      })
    }

    if (formData.department !== data!.Payload.SellerInformation.Department) {
      patchOperations.push({
        op: data!.Payload.SellerInformation.Department ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Department',
        value: formData.department,
      })
    }

    if (formData.name !== data!.Payload.SellerInformation.Contact?.Name) {
      patchOperations.push({
        op: data!.Payload.SellerInformation.Contact?.Name ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Name',
        value: formData.name,
      })
    }

    if (formData.phone !== data!.Payload.SellerInformation.Contact?.Phone) {
      patchOperations.push({
        op: data!.Payload.SellerInformation.Contact?.Phone ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Phone',
        value: formData.phone,
      })
    }

    if (formData.email !== data!.Payload.SellerInformation.Contact?.Email) {
      patchOperations.push({
        op: data!.Payload.SellerInformation.Contact?.Email ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Email',
        value: formData.email,
      })
    }

    if (formData.remarks !== data!.Payload.Remarks) {
      patchOperations.push({
        op: data!.Payload.Remarks ? 'replace' : 'add',
        path: '/Payload/Remarks',
        value: formData.remarks,
      })
    }

    return patchOperations
  }

  const onFormSubmit = async (formData: OrderConfirmationPatchDto) => {
    const patchData: PatchRequest<WebEdiDto<OrderResponse>> =
      patchFromFormValues(formData)
    await requestPatchOrderById(id, patchData) // wieder einblenden ist für ein Patch Request
    mutate()
  }

  // error // passt das mit error das keine Daten gefunden wurden oder eher error.message ausgeben?
  if (error) {
    return (
      <>
        <div className="flex justify-content-between flex-wrap mb-5">
          <div className="flex justify-content-start flex-wrap">
            <div className="cursor-pointer mr-2" onClick={() => navigate('/')}>
              <i className="pi pi-angle-left text-primary text-5xl"></i>
            </div>
            <div className="font-normal text-4xl">
              {t('common.errors.noData')}
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Title id={id} orderId={data?.Payload.OrderId!} />

      <Header id={id} />

      <OrderInformation id={id} />

      <OrderConfirmation
        data={dataToFormValues(data)}
        onSubmit={onFormSubmit}
        orderResponseId={id}
        isLoading={isLoading}
      />

      <Positions id={id} currency={data?.Payload.Currency} />

      <Attachment id={id} />
    </>
  )
}
